import { lazyPage } from "./lazy-page";
import { Suspense, useEffect, useMemo, useState } from "react";
import { Avatar, Badge, Button, Drawer, Dropdown, Empty, Layout, List, Menu, Popover, Space, Spin, Tag, Typography, message } from "antd";
import {
  AppstoreOutlined, AuditOutlined, BankOutlined, BellOutlined, BgColorsOutlined, DashboardOutlined, FileProtectOutlined, FileTextOutlined, LogoutOutlined, NotificationOutlined,
  MobileOutlined, SafetyCertificateOutlined, SendOutlined, ShopOutlined, TeamOutlined, UserOutlined, VideoCameraOutlined
} from "@ant-design/icons";
import { api, getToken, roleNames, setToken, type User } from "./api";
import LoginStudio from "./components/LoginStudio";
import { clearNativeBackgroundSync } from "./native-background-sync";
import { useData } from "./hooks";
import { notificationTarget } from "./notification-routing";
import "./notification.css";
import { marketingMenuGroups } from "./marketing-menu";
import { marketingMenuKeyForPage, marketingPageForKey } from "./marketing-navigation";
import { useMarketingNavigation } from "./use-marketing-navigation";

type AppNotification = {
  id: string;
  title: string;
  body: string;
  type?: string;
  status: "unread" | "read" | string;
  created_at: string;
  entity_type?: string | null;
  entity_id?: string | null;
};



// 旧登录页仅在专用入口加载，不把其表单依赖加入默认登录/管理壳。
const Login = lazyPage("login-original", () => import("./components/Login"));

const Dashboard = lazyPage("dashboard", () => import("./pages/Dashboard"));
const Operations = lazyPage("operations", () => import("./pages/Operations"));
const Customers = lazyPage("customers", () => import("./pages/Customers"));
const AiDesk = lazyPage("ai", () => import("./pages/AiDesk"));
const PageAgentAssistant = lazyPage("page-agent", () => import("./components/PageAgentAssistant"));
const AiModelSettings = lazyPage("ai-model-settings", () => import("./pages/AiModelSettings"));
const MarketingPages = lazyPage("marketing-pages", () => import("./pages/MarketingPages"));
const StatisticsReports = lazyPage("statistics-reports", () => import("./pages/StatisticsReports"));
const OrderPayments = lazyPage("order-payments", () => import("./pages/OrderPayments"));
const MobilePortal = lazyPage("mobile", () => import("./pages/MobilePortal"));
const AppReleaseManagement = lazyPage("app-management", () => import("./pages/AppReleaseManagement"));
const MobileDecoration = lazyPage("mobile-decoration", () => import("./pages/MobileDecoration"));
const PlatformConsole = lazyPage("platform", () => import("./pages/PlatformConsole"));
const TenantSaas = lazyPage("saas", () => import("./pages/TenantSaas"));
const AgencyContracts = lazyPage("agency-contracts", () => import("./pages/AgencyContracts"));
const ServiceQuality = lazyPage("service-quality", () => import("./pages/ServiceQuality"));
const RecruitmentOnboarding = lazyPage("recruitment-onboarding", () => import("./pages/RecruitmentOnboarding"));
const AddHousekeeper = lazyPage("add-housekeeper", () => import("./pages/AddHousekeeper"));
const CompleteHousekeeperProfile = lazyPage("complete-housekeeper-profile", () => import("./pages/CompleteHousekeeperProfile"));
const StoreEmployeeManagement = lazyPage("store-employee-management", () => import("./pages/StoreEmployeeManagement"));
const StoreManagement = lazyPage("store-management", () => import("./pages/StoreManagement"));
const ServiceStaffManagement = lazyPage("service-staff-management", () => import("./pages/ServiceStaffManagement"));
const PayrollCommission = lazyPage("payroll-commission", () => import("./pages/PayrollCommission"));
const CommunicationsCenter = lazyPage("communications-center", () => import("./pages/CommunicationsCenter"));
const SystemOperations = lazyPage("system-operations", () => import("./pages/SystemOperations"));
const AccountSecurity = lazyPage("account-security", () => import("./pages/AccountSecurity"));
const EnterpriseAcceptancePortal = lazyPage("enterprise-acceptance-portal", () => import("./pages/EnterpriseAcceptancePortal"));
const EnterpriseAccountPortal = lazyPage("enterprise-account-portal", () => import("./pages/EnterpriseAccountPortal"));
const SupplierPortal = lazyPage("supplier-portal", () => import("./pages/SupplierPortal"));
const OnboardingRegistration = lazyPage("onboarding-registration", () => import("./pages/OnboardingRegistration"));
const OnboardingRegistrationAdmin = lazyPage("onboarding-registration-admin", () => import("./pages/OnboardingRegistrationAdmin"));
const ProjectServices = lazyPage("project-services", () => import("./pages/ProjectServices"));
const ProjectManagement = lazyPage("project-management", () => import("./pages/ProjectManagement"));
const ProjectPackages = lazyPage("project-packages", () => import("./pages/ProjectPackages"));
const ProductOrders = lazyPage("product-orders", () => import("./pages/ProductOrders"));
const ProjectProducts = lazyPage("project-products", () => import("./pages/ProjectProducts"));
const AgencyContractTemplatesPage = lazyPage("agency-contract-templates", () => import("./pages/AgencyContractTemplatesPage"));
const Attendance = lazyPage("attendance", () => import("./pages/Attendance"));
const LeaveApproval = lazyPage("leave-approval", () => import("./pages/LeaveApproval"));
const SchedulingManagement = lazyPage("scheduling", () => import("./pages/SchedulingManagement"));
const VideoInterviews = lazyPage("video-interviews", () => import("./pages/VideoInterviews"));
const AgencyHousekeepers = lazyPage("agency-housekeepers", () => import("./pages/AgencyHousekeepers"));
const AgencyClients = lazyPage("agency-clients", () => import("./pages/AgencyClients"));
const AgencyMatches = lazyPage("agency-matches", () => import("./pages/AgencyMatches"));

const { Header, Sider, Content } = Layout;


const adminMenu = [
  { key: "dashboard", permission: "saas.read", icon: <DashboardOutlined />, label: "经营首页" },
  { key: "statisticsReports", permission: "saas.reports", icon: <DashboardOutlined />, label: "统计报表" },
  {
    key: "enterpriseSection",
    permission: "saas.enterprise",
    icon: <SafetyCertificateOutlined />,
    label: "企业管理",
    children: [
      { key: "saas", permission: "saas.subscription", icon: <FileProtectOutlined />, label: "企业与订阅" },
      { key: "storeManagement", permission: "saas.stores", icon: <ShopOutlined />, label: "门店管理" },
      { key: "storeEmployeeManagement", permission: "saas.employees", icon: <TeamOutlined />, label: "员工与账号" },
      { key: "communicationsCenter", permission: "notifications", icon: <NotificationOutlined />, label: "消息中心" }
    ]
  },
  {
    key: "homeServiceSection",
    permission: "home_service.read",
    icon: <ShopOutlined />,
    label: "家政到家",
    children: [
      { key: "operations", permission: "home_service.orders", icon: <ShopOutlined />, label: "服务订单" },
      { key: "dispatchOrders", permission: "home_service.dispatch", icon: <SendOutlined />, label: "派工单" },
      { key: "productOrders", permission: "home_service.orders", icon: <ShopOutlined />, label: "商品订单" },
      { key: "customers", permission: "home_service.customers", icon: <UserOutlined />, label: "到家客户" },
      { key: "projectManagement", permission: "home_service.projects", icon: <AppstoreOutlined />, label: "项目管理" },
      { key: "serviceStaffManagement", permission: "home_service.workers", icon: <TeamOutlined />, label: "服务人员" },
      { key: "payrollCommission", permission: "wages", icon: <BankOutlined />, label: "提成工资" },
      // 「入职登记与分享」不再占菜单位：页面顶部入口已移除，内部登记页仅保留路由。
      { key: "orderPayments", permission: "home_service.payments", icon: <BankOutlined />, label: "收款记录" },
      { key: "scheduling", permission: "home_service.dispatch", icon: <AuditOutlined />, label: "排班列表" },
      // 原「考勤管理」打卡明细页已从菜单隐藏：打卡明细页保留为内部页，不再提供菜单入口。
      // 请假审批页沿用 leaveApproval 路由，菜单统一显示为「考勤管理」。
      { key: "leaveApproval", permission: "home_service.attendance", icon: <AuditOutlined />, label: "考勤管理" },
      { key: "serviceQuality", permission: "home_service.quality", icon: <AuditOutlined />, label: "服务售后" }
      // 系统不提供主动离职申请：离职由「服务人员」「员工与账号」里停用或删除账号完成，离职审批页已下线。
    ]
  },
  {
    key: "agencySection",
    permission: "agency.read",
    icon: <TeamOutlined />,
    label: "家政中介",
    children: [
      { key: "agencyHousekeepers", permission: "agency.workers", icon: <TeamOutlined />, label: "家政员列表" },
      // 「入职登记与分享」同样不占菜单位：页面顶部入口已移除，内部登记页仅保留路由。
      { key: "agencyClients", permission: "agency.customers", icon: <UserOutlined />, label: "雇主需求" },
      // 「匹配工作台」已从菜单隐藏：页面、路由和匹配功能保留，暂不提供菜单入口。
      // { key: "agencyMatches", permission: "agency.matches", icon: <SwapOutlined />, label: "匹配工作台" },
      { key: "serviceContracts", permission: "agency.contracts", icon: <FileTextOutlined />, label: "订单合同" },
      { key: "videoInterviews", permission: "agency.interviews", icon: <VideoCameraOutlined />, label: "视频面试" },
      { key: "agencyContractTemplates", permission: "agency.contracts", roles: ["company_admin"], icon: <FileProtectOutlined />, label: "合同模板" }
    ]
  },
  {
    key: "marketingSection",
    permission: "marketing.read",
    icon: <AppstoreOutlined />,
    label: "活动营销",
    children: [
      ...marketingMenuGroups(),
      { key: "ai", permission: "marketing.ai", icon: <AppstoreOutlined />, label: "AI智能客服" },
      // 「招聘与入职」菜单入口已隐藏：页面、路由和刷新按钮保留，暂不提供菜单入口。
      { key: "mobile", permission: "marketing.mobile", icon: <MobileOutlined />, label: "移动端预览" }
    ]
  }
];
const infrastructureItem = { key: "infrastructure", icon: <AppstoreOutlined />, label: "系统运维" };
const platformMenu = [
  {
    key: "platform",
    icon: <AppstoreOutlined />,
    label: "SaaS平台",
    children: [
      { key: "platform-overview", icon: <DashboardOutlined />, label: "平台总览" },
      { key: "platform-tenantOverview", icon: <TeamOutlined />, label: "租户全景" },
      { key: "aiModelSettings", icon: <AppstoreOutlined />, label: "AI 模型设置" },
      { key: "appManagement", icon: <MobileOutlined />, label: "APP 管理" },
      { key: "mobileDecoration", icon: <BgColorsOutlined />, label: "移动端装修" },
      { key: "platform-tenants", icon: <AppstoreOutlined />, label: "租户管理" },
      { key: "platform-stores", icon: <ShopOutlined />, label: "门店管理" },
      { key: "platform-administrators", icon: <UserOutlined />, label: "平台主管理员" },
      { key: "agencyContractTemplates", icon: <FileProtectOutlined />, label: "通用合同模板" },
      { key: "platform-plans", icon: <AppstoreOutlined />, label: "套餐版本" },
      { key: "platform-addonCommerce", icon: <AppstoreOutlined />, label: "增购商品" },
      { key: "platform-invoices", icon: <BankOutlined />, label: "订阅账单" },
      // 以下四个超管页面已从 SaaS 平台菜单隐藏：页面、路由与接口保留，暂不提供菜单入口。
      // { key: "platform-billingCallbacks", icon: <NotificationOutlined />, label: "收款回调" },
      // { key: "platform-tenantOffboarding", icon: <LogoutOutlined />, label: "企业安全退出" },
      // { key: "platform-tenantDataErasure", icon: <FileProtectOutlined />, label: "数据清除治理" },
      // { key: "platform-developerApiRisk", icon: <AppstoreOutlined />, label: "开放 API 风险" },
      { key: "platform-videoQuota", icon: <VideoCameraOutlined />, label: "视频面试管理" }
    ]
  },
  infrastructureItem
];

function flattenMenuItems(items: any[]): any[] {
  return items.flatMap((item) => item.children?.length ? flattenMenuItems(item.children) : [item]);
}

function findMenuParents(items: any[], target: string, parents: string[] = []): string[] {
  for (const item of items) {
    if (item.key === target) return parents;
    if (item.children?.length) {
      const result = findMenuParents(item.children, target, [...parents, item.key]);
      if (result.length) return result;
    }
  }
  return [];
}

function collectMenuGroups(items: any[]): string[] {
  return items.flatMap((item) => item.children?.length ? [item.key, ...collectMenuGroups(item.children)] : []);
}

function filterMenuItems(items: any[], allowedKeys: string[], permissions: string[] = [], role = "", access?: User["businessAccess"]): any[] {
  return items.flatMap((item) => {
    const roleAllowed = !Array.isArray(item.roles) || item.roles.includes(role);
    const domainAllowed = !(String(item.permission || "").startsWith("agency.") && access?.agency === false) && !(String(item.permission || "").startsWith("home_service.") && access?.homeService === false);
    const permissionAllowed = roleAllowed && domainAllowed && (!item.permission || permissions.includes("*") || permissions.includes(String(item.permission)));
    if (!permissionAllowed) return [];
    if (item.children?.length) {
      const children = allowedKeys.includes(item.key)
        ? filterMenuItems(item.children, [], permissions, role, access)
        : filterMenuItems(item.children, allowedKeys, permissions, role, access);
      return children.length ? [{ ...item, children }] : [];
    }
    return !allowedKeys.length || allowedKeys.includes(item.key) ? [item] : [];
  });
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [checking, setChecking] = useState(Boolean(getToken()));
  const [page, setPage] = useState("dashboard");
  const [orderRequestId, setOrderRequestId] = useState("");
  const [orderRequestVisit, setOrderRequestVisit] = useState(0);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("wenjing-menu-open-keys") || "[]");
      return Array.isArray(saved) ? saved.filter((key): key is string => typeof key === "string") : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (!getToken()) return;
    void api<{ user: User; permissions?: string[] }>("/api/auth/me").then((result) => {
      setUser({ ...result.user, permissions: result.permissions ?? result.user.permissions });
      if (result.user.role === "platform_admin") setPage("platform-overview");
      else if (["worker", "customer"].includes(result.user.role)) setPage("mobile");
      else if (result.user.subscriptionStatus === "pending_review" || ["export_pending", "export_ready"].includes(String(result.user.offboardingStatus || ""))) setPage("saas");
    }).catch(() => setToken("")).finally(() => setChecking(false));
  }, []);

  const notificationData = useData<AppNotification[]>("/api/notifications", [], Boolean(user));
  const [markingNotificationsRead, setMarkingNotificationsRead] = useState(false);

  const menuItems = useMemo(() => {
    if (!user) return adminMenu;
    if (user.role === "platform_admin") return platformMenu;
    if (user.subscriptionStatus === "pending_review") return [{ key: "saas", icon: <SafetyCertificateOutlined />, label: "企业商用开通" }];
    if (["export_pending", "export_ready"].includes(String(user.offboardingStatus || ""))) return [{ key: "saas", icon: <SafetyCertificateOutlined />, label: "企业安全退出" }];
    if (["worker", "customer"].includes(user.role)) return [{ key: "mobile", icon: <MobileOutlined />, label: roleNames[user.role] + "端" }];
    const permissions = user.permissions || [];
    if (user.role === "company_admin") return [...filterMenuItems(adminMenu, [], permissions, user.role, user.businessAccess), infrastructureItem];
    return filterMenuItems(adminMenu, [], permissions, user.role, user.businessAccess);
  }, [user]);
  const quickMenuItems = useMemo(() => flattenMenuItems(menuItems).slice(0, 5), [menuItems]);
  const agencyPages = ["agencyHousekeepers", "agencyOnboarding", "agencyClients", "agencyMatches", "videoInterviews", "serviceContracts", "agencyContractTemplates"];
  const homePages = ["operations", "dispatchOrders", "productOrders", "orderPayments", "scheduling", "serviceQuality", "customers", "projectManagement", "projectServices", "projectPackages", "projectProducts", "serviceStaffManagement", "payrollCommission", "homeServiceOnboarding", "attendance", "leaveApproval"];
  // homeServiceOnboarding / agencyOnboarding 已从菜单移除，只作为人员主表页顶部的内部入口页；
  // 菜单过滤会去掉这两个键，因此入口按钮的可见性由按钮所在页面自身的板块权限门保证。
  const blockedPage = Boolean(user && (user.businessAccess?.agency === false && (agencyPages.includes(page) || /^(addHousekeeper|editHousekeeper:|completeHousekeeper:)/.test(page)) || user.businessAccess?.homeService === false && homePages.includes(page)));
  useEffect(() => { if (blockedPage) setPage("dashboard"); }, [blockedPage]);
  useEffect(() => {
    if (!user) return;
    let stopped = false, busy = false;
    const refresh = async () => {
      if (busy || document.visibilityState === "hidden") return;
      busy = true;
      try { const result = await api<{ user: User; permissions?: string[] }>("/api/auth/me"); if (!stopped) setUser(current => current?.id === result.user.id ? { ...result.user, permissions: result.permissions ?? result.user.permissions } : current); } catch { /* Protected APIs still enforce the current store configuration. */ } finally { busy = false; }
    };
    const onRefresh = () => { void refresh(); };
    const timer = window.setInterval(onRefresh, 30000);
    window.addEventListener("focus", onRefresh); window.addEventListener("store-capabilities-changed", onRefresh);
    return () => { stopped = true; window.clearInterval(timer); window.removeEventListener("focus", onRefresh); window.removeEventListener("store-capabilities-changed", onRefresh); };
  }, [user?.id]);
  useMarketingNavigation(page, setPage, flattenMenuItems(menuItems).map(item => String(item.key)), Boolean(user));

  useEffect(() => {
    const openSharedEmployee = () => {
      // 家政员档案分享链接：家政员管理页唯一且只有卡片样式那一个
      // （家政中介 → 家政员列表），因此直接进入该列表页。
      const sharedId = window.location.hash.match(/^#agency-housekeeper-(.+)$/)?.[1];
      if (!sharedId) return;
      const canOpen = Boolean(user && user.subscriptionStatus !== "pending_review" && !["export_pending", "export_ready"].includes(String(user.offboardingStatus || "")));
      if (canOpen && flattenMenuItems(menuItems).some((item) => item.key === "agencyHousekeepers")) setPage("agencyHousekeepers");
    };
    openSharedEmployee();
    window.addEventListener("hashchange", openSharedEmployee);
    return () => window.removeEventListener("hashchange", openSharedEmployee);
  }, [menuItems, user]);

  useEffect(() => {
    const allowedGroups = collectMenuGroups(menuItems);
    const activeParents = findMenuParents(menuItems, marketingMenuKeyForPage(page));
    setOpenKeys((current) => {
      const next = Array.from(new Set([...current.filter((key) => allowedGroups.includes(key)), ...activeParents]));
      try { localStorage.setItem("wenjing-menu-open-keys", JSON.stringify(next)); } catch { /* storage unavailable */ }
      return next;
    });
  }, [menuItems, page]);

  const handleMenuOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
    try { localStorage.setItem("wenjing-menu-open-keys", JSON.stringify(keys)); } catch { /* storage unavailable */ }
  };

  const publicPortalFallback = <div className="splash"><Spin size="large" /><Typography.Text>正在加载安全门户</Typography.Text></div>;
  if (location.pathname === "/supplier-portal") return <Suspense fallback={publicPortalFallback}><SupplierPortal /></Suspense>;
  if (location.pathname === "/enterprise-acceptance") return <Suspense fallback={publicPortalFallback}><EnterpriseAcceptancePortal /></Suspense>;
  if (location.pathname === "/enterprise-account") return <Suspense fallback={publicPortalFallback}><EnterpriseAccountPortal /></Suspense>;
  // 入职登记公开页：应聘者手机扫码/点链接进入，令牌在路径里，鉴权之前返回，因此无需登录。
  if (location.pathname.startsWith("/onboarding/")) return <Suspense fallback={publicPortalFallback}><OnboardingRegistration /></Suspense>;
  if (location.pathname === "/login-studio") return <LoginStudio onLogin={(nextUser) => { setUser(nextUser); setPage(nextUser.role === "platform_admin" ? "platform-overview" : nextUser.subscriptionStatus === "pending_review" || ["export_pending", "export_ready"].includes(String(nextUser.offboardingStatus || "")) ? "saas" : ["worker", "customer"].includes(nextUser.role) ? "mobile" : "dashboard"); window.history.replaceState({}, "", "/"); }} />;
  if (location.pathname === "/login-original") return <Login onLogin={(nextUser) => { setUser(nextUser); setPage(nextUser.role === "platform_admin" ? "platform-overview" : nextUser.subscriptionStatus === "pending_review" || ["export_pending", "export_ready"].includes(String(nextUser.offboardingStatus || "")) ? "saas" : ["worker", "customer"].includes(nextUser.role) ? "mobile" : "dashboard"); window.history.replaceState({}, "", "/"); }} />;
  if (checking) return <div className="splash"><Spin size="large" /><Typography.Text>正在加载问径智慧家政系统</Typography.Text></div>;
  if (!user) return <LoginStudio onLogin={(nextUser) => { setUser(nextUser); setPage(nextUser.role === "platform_admin" ? "platform-overview" : nextUser.subscriptionStatus === "pending_review" || ["export_pending", "export_ready"].includes(String(nextUser.offboardingStatus || "")) ? "saas" : ["worker", "customer"].includes(nextUser.role) ? "mobile" : "dashboard"); }} />;

  const logout = async () => {
    await clearNativeBackgroundSync(true).catch(() => undefined);
    await api("/api/auth/logout", { method: "POST", body: "{}" }).catch(() => null);
    setNotificationOpen(false);
    notificationData.setData([]);
    setToken(""); setUser(null);
  };

  const unreadNotifications = notificationData.data.filter((item) => item.status === "unread");
  const canOpenCommunications = flattenMenuItems(menuItems).some((item) => item.key === "communicationsCenter");
  const markAllNotificationsRead = async () => {
    if (markingNotificationsRead || unreadNotifications.length === 0) return;
    setMarkingNotificationsRead(true);
    try {
      await Promise.all(unreadNotifications.map((item) => api(`/api/notifications/${encodeURIComponent(item.id)}/read`, { method: "POST", body: "{}" })));
      await notificationData.reload();
    } catch (error) {
      message.error(error instanceof Error ? error.message : "通知已读状态更新失败");
    } finally { setMarkingNotificationsRead(false); }
  };
  const formatNotificationTime = (value: string) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };
  const openCommunications = () => {
    setNotificationOpen(false);
    setPage("communicationsCenter");
  };
  const handleNotificationClick = async (item: AppNotification) => {
    setNotificationOpen(false);
    if (item.status === "unread") {
      try {
        await api(`/api/notifications/${encodeURIComponent(item.id)}/read`, { method: "POST", body: "{}" });
        await notificationData.reload();
      } catch (error) {
        message.error(error instanceof Error ? error.message : "通知状态更新失败");
      }
    }
    const target = notificationTarget(item);
    if (target === "communicationsCenter" || !flattenMenuItems(menuItems).some((entry) => entry.key === target)) {
      if (canOpenCommunications) setPage("communicationsCenter");
    } else { if (item.entity_type === "customer_order_request") { setOrderRequestId(item.entity_id || ""); setOrderRequestVisit(value => value + 1); } setPage(target); }
  };
  const notificationPanel = (
    <div className="notification-panel">
      <div className="notification-panel-heading">
        <div>
          <Typography.Text strong>通知提醒</Typography.Text>
          <Typography.Text type="secondary">{unreadNotifications.length ? `${unreadNotifications.length} 条未读` : "已全部读完"}</Typography.Text>
        </div>
        <Space size={4}>
          <Button type="link" size="small" loading={markingNotificationsRead} disabled={!unreadNotifications.length || notificationData.loading} onClick={() => void markAllNotificationsRead()}>标为已读</Button>
        </Space>
      </div>
      {notificationData.error && <Typography.Text className="notification-error" type="danger">通知加载失败，请稍后重试</Typography.Text>}
      <List
        className="notification-list"
        split={false}
        loading={notificationData.loading && !notificationData.data.length}
        dataSource={notificationData.data.slice(0, 6)}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无通知" /> }}
        renderItem={(item) => (
          <List.Item className={`notification-item ${item.status === "unread" ? "unread" : ""}`}>
            <button type="button" className="notification-item-button" onClick={() => void handleNotificationClick(item)}>
              <span className="notification-item-dot" aria-hidden="true" />
              <span className="notification-item-copy">
                <span className="notification-item-title">{item.title || "系统通知"}{item.status === "unread" && <Tag color="blue">未读</Tag>}</span>
                <span className="notification-item-body">{item.body || "暂无详细内容"}</span>
                <span className="notification-item-time">{formatNotificationTime(item.created_at)}</span>
              </span>
            </button>
          </List.Item>
        )}
      />
      {canOpenCommunications && <div className="notification-panel-footer"><Button type="link" onClick={openCommunications}>查看全部通知</Button></div>}
    </div>
  );


  const current = user.subscriptionStatus === "pending_review" || ["export_pending", "export_ready"].includes(String(user.offboardingStatus || "")) ? <TenantSaas user={user} /> : page === "platform-stores" ? <StoreManagement user={user} /> : page.startsWith("platform-") || page === "platform" ? <PlatformConsole section={page} onNavigate={setPage} /> : page === "accountSecurity" ? <AccountSecurity /> : page === "saas" ? <TenantSaas user={user} /> : page === "infrastructure" ? <SystemOperations /> : page === "dashboard" ? <Dashboard role={user.role} user={user} allowedPageKeys={flattenMenuItems(menuItems).map(item => String(item.key))} onNavigate={setPage} /> : page === "customers" ? <Customers user={user} /> : (page === "operations" || page === "dispatchOrders") ? <Operations user={user} orderRequestId={orderRequestId} orderRequestVisit={orderRequestVisit} onRequestHandled={() => void notificationData.reload()} view={page === "dispatchOrders" ? "tasks" : "orders"} onNavigate={setPage} /> : page === "attendance" ? <Attendance user={user} /> : page === "leaveApproval" ? <LeaveApproval onNavigate={setPage} /> : page === "scheduling" ? <SchedulingManagement onNavigate={setPage} /> : page === "videoInterviews" ? <VideoInterviews /> : page === "agencyHousekeepers" ? <AgencyHousekeepers /> : page === "agencyClients" ? <AgencyClients /> : page === "agencyMatches" ? <AgencyMatches user={user} /> : page === "productOrders" ? <ProductOrders /> : page === "orderPayments" ? <OrderPayments /> : page === "serviceQuality" ? <ServiceQuality /> : page === "recruitmentOnboarding" ? <RecruitmentOnboarding /> : (page === "homeServiceOnboarding" || page === "agencyOnboarding") ? <OnboardingRegistrationAdmin user={user} board={page === "agencyOnboarding" ? "agency" : "home_service"} returnPage={page === "agencyOnboarding" ? "agencyHousekeepers" : "serviceStaffManagement"} onNavigate={setPage} /> : page === "serviceStaffManagement" ? <ServiceStaffManagement /> : page === "payrollCommission" ? <PayrollCommission /> : page === "addHousekeeper" ? <AddHousekeeper onNavigate={setPage} /> : page.startsWith("editHousekeeper:") ? <CompleteHousekeeperProfile mode="edit" workerId={page.slice("editHousekeeper:".length)} onNavigate={setPage} /> : page.startsWith("completeHousekeeper:") ? <CompleteHousekeeperProfile workerId={page.slice("completeHousekeeper:".length)} onNavigate={setPage} /> : page === "storeEmployeeManagement" ? <StoreEmployeeManagement /> : page === "storeManagement" ? <StoreManagement user={user} /> : page.startsWith("storeManagement:") ? <StoreManagement user={user} tenantId={page.slice("storeManagement:".length)} /> : page === "agencyContractTemplates" ? <AgencyContractTemplatesPage isPlatform={user.role === "platform_admin"} /> : page === "communicationsCenter" ? <CommunicationsCenter onOrderRequest={(id) => { if (flattenMenuItems(menuItems).some(entry => entry.key === "operations")) { setOrderRequestId(id); setOrderRequestVisit(value => value + 1); setPage("operations"); } else message.warning("当前账号没有订单审批权限，请联系门店管理员"); }} onNavigate={setPage} onNotificationsRead={() => notificationData.reload()} /> : page === "serviceContracts" ? <AgencyContracts /> : page === "projectManagement" ? <ProjectManagement /> : page === "projectServices" ? <ProjectServices /> : page === "projectPackages" ? <ProjectPackages /> : page === "projectProducts" ? <ProjectProducts /> : page === "aiModelSettings" ? <AiModelSettings user={user} /> : page === "ai" ? <AiDesk onNavigate={setPage} /> : marketingPageForKey(page) ? <MarketingPages page={page} user={user} allowedKeys={flattenMenuItems(menuItems).map(item => String(item.key))} /> : page === "statisticsReports" ? <StatisticsReports /> : <MobilePortal user={user} allowedPageKeys={flattenMenuItems(menuItems).map(item => String(item.key))} onNavigate={setPage} />;
  const navigation = <Menu className="app-navigation" mode="inline" theme="dark" inlineIndent={16} aria-label="主导航菜单" defaultOpenKeys={[]} selectedKeys={[marketingMenuKeyForPage(page)]} openKeys={openKeys} onOpenChange={handleMenuOpenChange} items={menuItems} onClick={({ key }) => { setPage(String(key)); setMobileMenu(false); }} />;

  return <Layout className="app-layout">
    <Sider className="desktop-sider" width={244}>
      <div className="brand"><img src="/logo/安居智慧物业-icon.svg" alt="问径智慧家政" /><div><strong>问径智慧家政</strong><span>WENJING SERVICE OS</span></div></div>
      {navigation}
    </Sider>
    <Layout>
      <Header className="app-header">
        <Button type="text" className="mobile-toggle" aria-label="打开功能导航" title="打开功能导航" icon={<AppstoreOutlined />} onClick={() => setMobileMenu(true)} />
        <div className="header-context"><Typography.Text strong>{roleNames[user.role]}</Typography.Text><Typography.Text type="secondary"> · {user.storeName || user.tenantName}</Typography.Text></div>
        <Space size="middle">
          {!["worker", "customer"].includes(user.role) && user.subscriptionStatus !== "pending_review" && !["export_pending", "export_ready"].includes(String(user.offboardingStatus || "")) && <Suspense fallback={null}><PageAgentAssistant key={`${user.tenantId}:${user.id}:${user.role}:${user.storeId}`} user={user} /></Suspense>}
<Popover content={notificationPanel} trigger="click" placement="bottomRight" open={notificationOpen} onOpenChange={setNotificationOpen} overlayStyle={{ width: 380, maxWidth: "calc(100vw - 28px)" }}>
            <Badge count={unreadNotifications.length} size="small" overflowCount={99}>
              <Button type="text" className="header-notification-button" aria-label="查看通知" aria-haspopup="dialog" aria-expanded={notificationOpen} title="查看通知" icon={<BellOutlined />} onClick={() => setNotificationOpen(true)} />
            </Badge>
          </Popover>
          <Dropdown menu={{ items: [{ key: "profile", icon: <UserOutlined />, label: `${user.name} · ${user.phone}` }, { key: "security", icon: <SafetyCertificateOutlined />, label: "账号与登录设备", onClick: () => setPage("accountSecurity") }, { type: "divider" }, { key: "logout", icon: <LogoutOutlined />, label: "退出登录", onClick: logout }] }}>
            <Space className="user-chip"><Avatar icon={<UserOutlined />} /><div><strong>{user.name}</strong><span>{roleNames[user.role]}</span></div></Space>
          </Dropdown>
        </Space>
      </Header>
      <Content key={`${user.id}:${user.storeId}:${user.businessAccess?.agency}:${user.businessAccess?.homeService}`} className="app-content"><Suspense fallback={<div className="splash"><Spin size="large" /><Typography.Text>正在加载业务模块</Typography.Text></div>}>{blockedPage ? null : page === "appManagement" ? <AppReleaseManagement user={user} /> : page === "mobileDecoration" ? <MobileDecoration user={user} /> : current}</Suspense></Content>
      <div className="mobile-bottom-nav">{quickMenuItems.map((item) => <button className={page === item.key ? "active" : ""} key={item.key} onClick={() => setPage(item.key)}>{item.icon}<span>{item.label}</span></button>)}</div>
    </Layout>
    <Drawer title="功能导航" placement="left" open={mobileMenu} onClose={() => setMobileMenu(false)} styles={{ body: { padding: 0, background: "#0b2f2c" } }}>{navigation}</Drawer>
  </Layout>;
}
